import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip, { type MetaChipCustomTone } from "../../../../../components/UIToolkit/Badges/MetaChip";
import { ALPHA, ICON_SIZE, INK, JOB_NOTIFICATION_CHIP, MOTION, SURFACE } from "../../../../../theme/tokens";

type Props = {
	jobName: string;
	isPrimary: boolean;
	/** `undefined` means the state isn't known yet, so the chip shows only the name and can't be toggled. */
	isSilent: boolean | undefined;
	/** True when the employee made the last change from the app. */
	changedByEmployee: boolean;
	/** False when the viewer can't edit this profile. The chip still shows the state. */
	canToggle: boolean;
	onToggle: () => void;
	dataTestId: string;
};

const useChipTone = (isPrimary: boolean, isSilent: boolean | undefined): MetaChipCustomTone | undefined => {
	const theme = useTheme();
	if (isSilent === true) {
		return {
			ink: INK.MUTED,
			bg: JOB_NOTIFICATION_CHIP.SILENT_FILL,
			// A silent primary job keeps a brand border so it still looks primary.
			border: isPrimary ? alpha(theme.palette.primary.main, ALPHA.STATUS_BORDER) : JOB_NOTIFICATION_CHIP.SILENT_BORDER,
		};
	}
	// The primary job uses MetaChip's own brand tone.
	if (isPrimary) return undefined;
	return { ink: INK.PRIMARY, bg: SURFACE, border: JOB_NOTIFICATION_CHIP.ON_BORDER };
};

/** Replays the bell swing only when a job turns back on, not on first render. */
const useSwingKey = (isSilent: boolean | undefined): number => {
	const previous = useRef(isSilent);
	const [swingKey, setSwingKey] = useState(0);
	useEffect(() => {
		if (previous.current === true && isSilent === false) setSwingKey((key) => key + 1);
		previous.current = isSilent;
	}, [isSilent]);
	return swingKey;
};

/** One job chip on the profile. Clicking it turns open-shift notifications for that job on or off. */
export const JobNotificationChip: FC<Props> = ({ jobName, isPrimary, isSilent, changedByEmployee, canToggle, onToggle, dataTestId }) => {
	const { t } = useTranslation();
	const customTone = useChipTone(isPrimary, isSilent);
	const swingKey = useSwingKey(isSilent);
	const isKnown = isSilent !== undefined;
	const isInteractive = canToggle && isKnown;

	const tooltip = isKnown
		? [
				t(isSilent ? "jobNotifications.profile.chip.silent" : "jobNotifications.profile.chip.on", { job: jobName }),
				isInteractive ? t(isSilent ? "jobNotifications.profile.chip.clickToTurnOn" : "jobNotifications.profile.chip.clickToMute") : "",
				changedByEmployee ? t(isSilent ? "jobNotifications.profile.chip.mutedByEmployee" : "jobNotifications.profile.chip.turnedOnByEmployee") : "",
			]
				.filter(Boolean)
				.join(" ")
		: undefined;

	const bell = isKnown && (
		<Box
			key={swingKey}
			component="span"
			aria-hidden
			sx={{
				display: "inline-flex",
				flexShrink: 0,
				"& > svg": { fontSize: ICON_SIZE.PILL },
				...(swingKey > 0 && {
					transformOrigin: "50% 10%",
					"@keyframes jobBellSwing": {
						"0%": { transform: "rotate(0deg)" },
						"30%": { transform: `rotate(${JOB_NOTIFICATION_CHIP.BELL_SWING_DEG}deg)` },
						"65%": { transform: `rotate(-${JOB_NOTIFICATION_CHIP.BELL_SWING_DEG / 2}deg)` },
						"100%": { transform: "rotate(0deg)" },
					},
					animation: `jobBellSwing ${JOB_NOTIFICATION_CHIP.BELL_SWING_MS}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { animation: "none" },
				}),
			}}
		>
			{isSilent ? <NotificationsOffOutlinedIcon /> : <NotificationsNoneOutlinedIcon />}
		</Box>
	);

	return (
		<MetaChip
			label={jobName}
			icon={bell || undefined}
			iconPlacement="end"
			tone="primary"
			customTone={customTone}
			size="medium"
			tooltip={tooltip}
			onClick={isInteractive ? onToggle : undefined}
			ariaPressed={isInteractive ? !isSilent : undefined}
			ariaLabel={isInteractive ? t("jobNotifications.profile.notificationsFor", { job: jobName }) : undefined}
			dataTestId={dataTestId}
			sx={{ fontWeight: isSilent ? 500 : 600 }}
		/>
	);
};
